import React, { useState } from 'react';
import {
  Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import {
  Plus, Wallet, TrendingUp, TrendingDown, ArrowDownRight, ArrowUpRight, Clock,
} from 'lucide-react';
import {
  PageHeader, KpiCard, StatusChip, Panel, ProgressRow, FormDialog, LoadGate, PageShell, CHART_FONT,
} from './businessUi';
import { formatCurrency } from './erpData';
import { useErp } from './useErp';
import { ERP_KEYS } from '../../Services/app/erpservice';

// Finance reads its own ledger plus sales/purchase to surface receivables & payables.
const KEYS = { txns: ERP_KEYS.finance, sales: ERP_KEYS.sales, purchase: ERP_KEYS.purchase };

const statusTone = { Paid: 'success', Pending: 'warning' };
const EXPENSE_CATEGORIES = ['Raw Material', 'Salaries', 'Utilities', 'Maintenance', 'Logistics', 'Rent', 'Other'];
const INCOME_CATEGORIES = ['Sales', 'Services', 'Interest', 'Other'];

const ADD_FIELDS = [
  { name: 'type', label: 'Type', type: 'select', default: 'Expense', options: ['Income', 'Expense'] },
  { name: 'category', label: 'Category', type: 'select', default: 'Raw Material', options: Array.from(new Set([...EXPENSE_CATEGORIES, ...INCOME_CATEGORIES])) },
  { name: 'party', label: 'Party', helper: 'Customer / supplier / payee' },
  { name: 'amount', label: 'Amount (INR)', type: 'number', default: 0 },
  { name: 'date', label: 'Date', type: 'date', default: '2026-06-13' },
  { name: 'method', label: 'Method', type: 'select', default: 'Bank', options: ['Bank', 'Cash', 'UPI', 'Cheque', 'Card'] },
  { name: 'status', label: 'Status', type: 'select', default: 'Paid', options: ['Paid', 'Pending'] },
];

export default function Finance() {
  const { data, loading, error, save, reload } = useErp(KEYS);
  const [dialogOpen, setDialogOpen] = useState(false);

  const txns = data?.txns || [];
  const sales = data?.sales || [];
  const purchase = data?.purchase || [];

  const handleAdd = (v) => {
    const next = [{ id: `TXN-${4000 + txns.length + 1}`, ...v }, ...txns];
    save('txns', next).catch(() => window.alert('Failed to save to ThingsBoard. Please retry.'));
  };

  const isIncome = (t) => t.type === 'Income';
  const incomePaid = txns.filter((t) => isIncome(t) && t.status === 'Paid').reduce((s, t) => s + (Number(t.amount) || 0), 0);
  const expensePaid = txns.filter((t) => !isIncome(t) && t.status === 'Paid').reduce((s, t) => s + (Number(t.amount) || 0), 0);
  const netCash = incomePaid - expensePaid;
  const pending = txns.filter((t) => t.status === 'Pending').reduce((s, t) => s + (Number(t.amount) || 0), 0);

  // Receivables (unpaid sales) & payables (open purchase) from the other modules.
  const receivable = sales.filter((o) => o.status !== 'Paid').reduce((s, o) => s + (Number(o.amount) || 0), 0);
  const payable = purchase.filter((o) => o.status === 'Open' || o.status === 'Partially Received').reduce((s, o) => s + (Number(o.amount) || 0), 0);

  // Cash-flow over time: income vs expense by date (paid only).
  const byDate = {};
  txns.filter((t) => t.status === 'Paid').forEach((t) => {
    const d = String(t.date);
    if (!byDate[d]) byDate[d] = { income: 0, expense: 0 };
    byDate[d][isIncome(t) ? 'income' : 'expense'] += Number(t.amount) || 0;
  });
  const dates = Object.keys(byDate).sort((a, b) => a.localeCompare(b));
  const flowOptions = {
    chart: { type: 'area', toolbar: { show: false }, fontFamily: CHART_FONT, dropShadow: { enabled: true, top: 6, blur: 6, opacity: 0.1 } },
    stroke: { curve: 'smooth', width: 3 },
    fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.3, opacityTo: 0.04 } },
    colors: ['#10b981', '#f43f5e'],
    dataLabels: { enabled: false },
    xaxis: { categories: dates.map((d) => d.slice(5)), labels: { style: { fontSize: '11px' } } },
    yaxis: { labels: { formatter: (v) => `${Math.round(v / 1000)}k`, style: { fontSize: '11px' } } },
    tooltip: { y: { formatter: (v) => formatCurrency(v) } },
    grid: { borderColor: 'background.default', strokeDashArray: 4 },
    legend: { position: 'top', fontSize: '12px' },
    markers: { size: 3, strokeWidth: 0 },
  };
  const flowSeries = [
    { name: 'Income', data: dates.map((d) => byDate[d].income) },
    { name: 'Expense', data: dates.map((d) => byDate[d].expense) },
  ];

  // Expense breakdown by category (paid).
  const byCat = {};
  txns.filter((t) => !isIncome(t) && t.status === 'Paid').forEach((t) => {
    const c = t.category || 'Other';
    byCat[c] = (byCat[c] || 0) + (Number(t.amount) || 0);
  });
  const catEntries = Object.entries(byCat).sort((a, b) => b[1] - a[1]);
  const maxCat = Math.max(1, ...catEntries.map((c) => c[1]));
  const catTones = ['primary', 'sky', 'warning', 'fuchsia', 'destructive', 'success'];

  return (
    <PageShell>
      <PageHeader
        title="Finance"
        subtitle="Cash movement — income, expenses, receivables & payables"
        actions={<Button variant="contained" size="small" startIcon={<Plus size={16} />} onClick={() => setDialogOpen(true)}>New Transaction</Button>}
      />

      <FormDialog open={dialogOpen} title="New Transaction" fields={ADD_FIELDS} onClose={() => setDialogOpen(false)} onSubmit={handleAdd} submitLabel="Record" />

      <LoadGate loading={loading} error={error} onRetry={reload}>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, mb: 2.5 }}>
          <KpiCard label="Income (Paid)" value={formatCurrency(incomePaid)} icon={TrendingUp} tone="success" />
          <KpiCard label="Expense (Paid)" value={formatCurrency(expensePaid)} icon={TrendingDown} tone="destructive" />
          <KpiCard label="Net Cash" value={formatCurrency(netCash)} icon={Wallet} tone={netCash >= 0 ? 'primary' : 'warning'} hint={netCash >= 0 ? 'surplus' : 'deficit'} />
          <KpiCard label="Pending" value={formatCurrency(pending)} icon={Clock} tone="warning" hint="awaiting settlement" />
        </Box>

        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, mb: 2.5 }}>
          <KpiCard label="Accounts Receivable" value={formatCurrency(receivable)} icon={ArrowDownRight} tone="sky" hint="unpaid sales orders" />
          <KpiCard label="Accounts Payable" value={formatCurrency(payable)} icon={ArrowUpRight} tone="fuchsia" hint="open purchase orders" />
        </Box>

        {txns.length === 0 ? (
          <Panel title="Transactions" accent="linear-gradient(90deg,#10b981,#34d399)">
            <Typography sx={{ p: 4, textAlign: 'center', color: 'text.disabled', fontSize: 14 }}>
              No transactions yet. Click <strong>New Transaction</strong> to record money in or out — it’s saved to ThingsBoard.
              {(receivable > 0 || payable > 0) && <><br />Receivables and payables above are pulled live from Sales &amp; Purchase.</>}
            </Typography>
          </Panel>
        ) : (
          <>
            <Panel title="Cash Flow" subtitle="Income vs expense over time (settled)" accent="linear-gradient(90deg,#10b981,#34d399)" sx={{ mb: 2.5 }}>
              <Box sx={{ px: 2, pb: 1 }}>
                <ReactApexChart options={flowOptions} series={flowSeries} type="area" height={260} />
              </Box>
            </Panel>

            <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, mb: 2.5 }}>
              <Panel title="Expense by Category" subtitle="Where the money goes" accent="linear-gradient(90deg,#f59e0b,#fbbf24)">
                <Box sx={{ px: 2, pt: 1.5, pb: 2 }}>
                  {catEntries.length === 0 && <Typography sx={{ fontSize: 13, color: 'text.disabled', py: 2 }}>No settled expenses yet.</Typography>}
                  {catEntries.map(([cat, amt], i) => (
                    <ProgressRow key={cat} label={cat} value={amt} max={maxCat} tone={catTones[i % catTones.length]} valueLabel={formatCurrency(amt)} />
                  ))}
                </Box>
              </Panel>

              <Panel title="Recent Transactions" subtitle={`${txns.length} record(s)`} accent="linear-gradient(90deg,#0ea5e9,#38bdf8)">
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Party</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {txns.map((t) => {
                        const income = isIncome(t);
                        return (
                          <TableRow key={t.id} hover>
                            <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{t.date}</TableCell>
                            <TableCell>
                              {t.party || '—'}
                              <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{t.method}</Typography>
                            </TableCell>
                            <TableCell><StatusChip label={t.category} tone="secondary" /></TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: income ? '#047857' : '#be123c', fontVariantNumeric: 'tabular-nums' }}>
                              {income ? '+' : '−'}{formatCurrency(Number(t.amount) || 0)}
                            </TableCell>
                            <TableCell><StatusChip label={t.status} tone={statusTone[t.status]} /></TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Panel>
            </Box>
          </>
        )}
      </LoadGate>
    </PageShell>
  );
}
